import { useState, useEffect, useMemo, useCallback } from "react";
import s from "./CreateEvent.module.scss";
import { Poster } from "./components/Poster/Poster";
import { MainDescription } from "./components/Description/MainDescription";
import { TicketOnEvent } from "./components/Tickets/TicketOnEvent";
import { Promo } from "./components/Promo/Promo";
import { CategorySection } from "./components/CategorySection/CategorySection";
import Loader from "../../components/Loader/Loader";
import { useNavigate } from "react-router-dom";
import { EventOrganizer, FormDataType } from "../../types/types";
import ModalNoAuth from "../../components/ui/ModalNoAuth/ModalNoAuth";
import defaultBackground from "./Images/background.svg";
import closeBtn from "..//..//icons/closeIcon.svg";
import { API_BASE } from "../../config/api";
import { isValidRussianPhone } from "utils/phones";

interface CreateEventProps {
  editableEvent?: EventOrganizer;
}

export const CreateEvent = ({ editableEvent }: CreateEventProps) => {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    navigate("/"); // Перенаправляем на главную страницу при закрытии
  }, [navigate]);

  const handleOverlayClick = useCallback(() => {
    closeModal(); // Используем функцию закрытия
  }, [closeModal]);

  const getAuthToken = (): string | null => {
    const cookies = document.cookie;
    const cookiesArray = cookies.split(";");
    for (const cookie of cookiesArray) {
      const [name, value] = cookie.trim().split("=");
      if (name === "authToken") return value;
    }
    return null;
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = getAuthToken();
        const userId = localStorage.getItem("userId");
        const response = await fetch(`${API_BASE}/users/profile/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        if (response.ok) {
          setIsUserLoggedIn(true);
          setIsModalOpen(false); // Закрываем модальное окно если пользователь авторизован
        } else {
          setIsUserLoggedIn(false);
          setIsModalOpen(true); // Показываем модальное окно если пользователь не авторизован
        }
      } catch (error) {
        console.error("Проверка авторизации не удалась:", error);
        setIsUserLoggedIn(false);
        setIsModalOpen(true);
      }
    };

    checkAuth();
  }, []);

  const token = getAuthToken();

  const [errors, setErrors] = useState({
    main: false,
    categories: false,
    photos: false,
    tickets: false,
  });

  const [formData, setFormData] = useState<FormDataType>({
    name: "",
    description: "Организатор не указал информацию",
    starts_at: "2025-04-07T18:00",
    ends_at: "2025-04-07T20:00",
    date: "2025-04-07",
    time: "18:00",
    endDate: "2025-04-07",
    endTime: "20:00",
    place: {
      name: "",
      city: "",
      address: "",
    },
    hide_address: false,
    phone: "",
    categories: [1],
    cover: "https://example.com/cover.jpg",
    photos: ["https://example.com/photo1.jpg"],
    video_url: "",
    social_media_links: [],
    review: { url: "" },
    ticket_types: [
      {
        name: "",
        price: 0,
        max_count: 0,
        sorter: 1,
      },
    ],
    promocodes: [],
  });

  const [originalFormData, setOriginalFormData] = useState<
    FormDataType | undefined
  >();

  useEffect(() => {
    if (!editableEvent) return;

    const cover = editableEvent.cover_medium ?? "";
    const photos = editableEvent.photos_data?.map((p) => p.medium) ?? [];
    const end = editableEvent.ends_at ?? editableEvent.starts_at;

    const updatedFormData: FormDataType = {
      name: editableEvent.name ?? "",
      description: editableEvent.description ?? "Описание",
      starts_at: editableEvent.starts_at.slice(0, 16),
      ends_at: (editableEvent.ends_at ?? editableEvent.starts_at).slice(0, 16),
      date: editableEvent.starts_at.split("T")[0],
      time: editableEvent.starts_at.split("T")[1].slice(0, 5),
      endDate: end.split("T")[0],
      endTime: end.split("T")[1].slice(0, 5),

      place: {
        name: editableEvent.place.name ?? "",
        city: editableEvent.place.city ?? "",
        address: editableEvent.place.address ?? "",
      },

      hide_address: editableEvent.hide_address ?? false,
      phone: editableEvent.phone ?? "",

      categories: editableEvent.categories_info.map((c) => c.id),

      cover,
      photos,

      video_url: "",
      social_media_links: editableEvent.social_media_links_data.map((link) => ({
        id: link.id,
        url: link.url,
      })),
      review: {
        id: editableEvent.review_data?.id,
        url: editableEvent.review_data?.url ?? "",
      },
      ticket_types: editableEvent.ticket_types_data.map(
        ({ id, name, description, price, max_count, sorter }) => ({
          id,
          name,
          description,
          price,
          max_count,
          sorter,
        })
      ),
      promocodes:
        editableEvent.promocodes_info?.map(({ id, name, discount }) => ({
          id,
          name,
          discount,
        })) ?? [],
    };

    setFormData(updatedFormData);
    setOriginalFormData(updatedFormData);
  }, [editableEvent]);

  // 👇 добавлено: отключаем загрузку
  useEffect(() => {
    setLoading(false);
  }, []);

  const editablePhotosId = useMemo(() => {
    return editableEvent?.photos_data?.map((photo) => photo.id);
  }, [editableEvent?.photos_data]);
  const editableSocialId = useMemo(() => {
    return originalFormData?.social_media_links?.map((sm) => sm.id);
  }, [originalFormData?.social_media_links]);
  const editableReviewId = useMemo(() => {
    return originalFormData?.review?.id;
  }, [originalFormData?.review]);
  const editablePromocodesId = useMemo(() => {
    return originalFormData?.promocodes?.map((promo) => promo.id);
  }, [originalFormData?.promocodes]);
  const editableTicketTypesId = useMemo(() => {
    return originalFormData?.ticket_types?.map((ticket) => ticket.id);
  }, [originalFormData?.ticket_types]);

  const validateForm = (): boolean => {
    const newErrors = {
      main:
        formData.name === "" ||
        !formData.starts_at ||
        !formData.ends_at ||
        formData.phone === "" ||
        !isValidRussianPhone(formData.phone) ||
        formData.place.name === "" ||
        formData.place.city === "" ||
        formData.place.address === "",
      categories: formData.categories.length === 0,
      photos:
        formData.photos.length === 0 || formData.cover === defaultBackground,
      tickets: formData.ticket_types.some(
        (t) => t.name.trim() === "" || t.price <= 0 || t.max_count <= 0
      ),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const handlePublish = async () => {
    const starts_at = `${formData.date}T${formData.time}`;
    const ends_at = `${formData.endDate}T${formData.endTime}`;

    if (!validateForm()) {
      alert("Пожалуйста, заполните все обязательные поля корректно");
      return;
    }

    try {
      const requestData = {
        ...formData,
        starts_at,
        ends_at,
      };

      if (requestData.video_url === "") {
        delete requestData.video_url;
      }
      if (requestData.review?.url === "") {
        delete requestData.review;
      }
      delete requestData.date;
      delete requestData.time;
      delete requestData.endDate;
      delete requestData.endTime;

      let patchRequestData = {};
      if (originalFormData) {
        const compareFormsData = (
          original: FormDataType,
          newData: FormDataType
        ) => {
          const flatFields = [
            "name",
            "description",
            "starts_at",
            "ends_at",
            "hide_address",
            "phone",
            "cover",
            "categories",
          ] as const;
          const changes: any = {};
          flatFields.forEach((field) => {
            if (
              JSON.stringify(original[field]) !== JSON.stringify(newData[field])
            ) {
              changes[field] = newData[field];
            }
          });

          if (originalFormData.photos !== newData.photos) {
            changes.photos = [];
            newData.photos.forEach((photo, index) => {
              if (photo.startsWith("data:image/")) {
                changes.photos[index] = photo;
              }
              if (
                editablePhotosId?.[index] !== undefined &&
                !photo.startsWith("data:image/")
              ) {
                changes.photos[index] = editablePhotosId?.[index];
              }
            });
          }

          if (
            original.place.name !== newData.place.name ||
            original.place.city !== newData.place.city ||
            original.place.address !== newData.place.address
          ) {
            changes.place = newData.place;
          }

          if (original.review?.url !== newData.review?.url) {
            changes.review = newData.review;
          }

          const areObjectArraysEqual = (
            original: any[],
            newData: any[],
            keysToCompare: string[]
          ) => {
            if (original.length !== newData.length) return false;
            return original.every((origItem, index) => {
              const newItem = newData[index];
              return keysToCompare.every(
                (key) =>
                  JSON.stringify(origItem[key]) === JSON.stringify(newItem[key])
              );
            });
          };

          if (
            !areObjectArraysEqual(original.ticket_types, newData.ticket_types, [
              "name",
              "price",
              "max_count",
              "description",
            ])
          ) {
            changes.ticket_types = newData.ticket_types;
          }
          if (
            !areObjectArraysEqual(original.promocodes, newData.promocodes, [
              "name",
              "discount",
            ])
          ) {
            changes.promocodes = newData.promocodes;
          }
          if (
            !areObjectArraysEqual(
              original.social_media_links,
              newData.social_media_links,
              ["url"]
            )
          ) {
            changes.social_media_links = newData.social_media_links;
          }

          return changes;
        };

        patchRequestData = compareFormsData(originalFormData, requestData);
      }

      console.log("Патч", patchRequestData);
      console.log("Оригинальные данные", originalFormData);
      console.log("Измененные данные", requestData);

      const isCreate = editableEvent === undefined;

      const response = await fetch(
        `${API_BASE}/events/${isCreate ? "create" : editableEvent.id}/`,
        {
          method: isCreate ? "POST" : "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(isCreate ? requestData : patchRequestData),
        }
      );

      if (!response.ok) {
        const errText = await response.text();
        console.error("Ответ с ошибкой:", errText);

        let message = "Ошибка при создании события";

        try {
          const json = JSON.parse(errText);

          if (typeof json.detail === "string") {
            message = json.detail;
          } else if (typeof json.ticket_types === "string") {
            // наш случай с билетами
            message = json.ticket_types;
          } else if (typeof json.message === "string") {
            message = json.message;
          } else if (Array.isArray(json.non_field_errors)) {
            message = json.non_field_errors.join("\n");
          }
        } catch {
          // если это не JSON, покажем сырой текст
          if (errText) {
            message = errText;
          }
        }

        throw new Error(message);
      }

      navigate("/");
    } catch (error: any) {
      console.error("Ошибка:", error);
      alert(error?.message ?? "Произошла ошибка при сохранении события");
    }
  };

  if (loading) return <Loader />;

  return (
    <div className={s.background}>
      <div className={s.container}>
        {isUserLoggedIn === true ? (
          <div className={s.columns}>
            <Poster
              editableCover={editableEvent ? formData.cover : null}
              editablePhotos={editableEvent ? formData.photos : null}
              setFormData={setFormData}
              hasError={errors.photos}
            />
            <div className={s.rightColumn}>
              <MainDescription
                formData={formData}
                editableSocialId={editableSocialId}
                editableReviewId={editableReviewId}
                setFormData={setFormData}
                hasError={errors.main || errors.categories || errors.photos}
              />
              <div className={s.MainTickets}>
                <TicketOnEvent
                  editableTicketTypes={
                    editableEvent ? formData.ticket_types : null
                  }
                  editableTicketTypesId={editableTicketTypesId}
                  setFormData={setFormData}
                  hasError={errors.tickets}
                />
                <Promo
                  editablePromocodes={
                    editableEvent ? formData.promocodes : null
                  }
                  editablePromocodesId={editablePromocodesId}
                  setFormData={setFormData}
                />
                <CategorySection
                  editableCategories={
                    editableEvent ? formData.categories : null
                  }
                  setFormData={setFormData}
                  hasError={errors.categories}
                />

                <button onClick={handlePublish} className={s.publishButton}>
                  Опубликовать событие
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {isModalOpen && (
              <div className="modal-overlay" onClick={handleOverlayClick}>
                <div className="modal-content">
                  <div className="modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modalCloseBtn">
                      <button
                        type="button"
                        className="closeBtn"
                        onClick={closeModal} // Используем исправленную функцию закрытия
                      >
                        <img src={closeBtn} alt="Закрыть" />
                      </button>
                    </div>
                    <ModalNoAuth />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
